import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search, LogOut, Sun, Moon, Trophy, Award, ShieldCheck, Users,
  Wrench, CheckCircle2, PlayCircle, BadgeCheck, GraduationCap, Lock,
} from "lucide-react";

// Official-style brand icons (hosted locally under /public/lab-icons).
const ICON_IMG = {
  linux: "/lab-icons/linux.jpg",
  azure: "/lab-icons/azure.jpg",
  aws: "/lab-icons/aws.jpg",
  windows: "/lab-icons/windows.jpg",
  sql: "/lab-icons/sqlserver.jpg",
  "interview-prep": "/lab-icons/interview-prep.jpg",
};

export default function LabPortal() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("All");

  const load = async () => { const { data } = await api.get("/portal/labs"); setData(data); };
  useEffect(() => { load(); }, []);

  const launch = async (lab, to) => {
    if (lab.availability === "maintenance") return;
    try { await api.post(`/portal/labs/${lab.id}/touch`); } catch { /* non-blocking */ }
    navigate(to || lab.route);
  };

  const labs = useMemo(() => data?.labs || [], [data]);
  const categories = ["All", ...(data?.categories || [])];
  const filtered = useMemo(() => labs.filter((l) => {
    const q = query.trim().toLowerCase();
    const matchQ = !q || l.name.toLowerCase().includes(q) || l.short.toLowerCase().includes(q) || l.category.toLowerCase().includes(q);
    return (cat === "All" || l.category === cat) && matchQ;
  }), [labs, query, cat]);

  const isStaff = data?.role === "admin" || data?.role === "instructor";

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="lab-portal">
      {/* top bar */}
      <header className="h-16 border-b border-border bg-card flex items-center gap-4 px-5 sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-primary grid place-items-center text-primary-foreground font-display font-extrabold">H</div>
          <div>
            <div className="font-display font-extrabold text-base leading-none">HMG Global Consults</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">Hands-On Training Labs</div>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <button onClick={() => navigate("/leaderboard")} data-testid="portal-leaderboard" className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-accent"><Trophy className="h-4 w-4" /> Leaderboard</button>
          <button onClick={() => navigate("/certificates")} data-testid="portal-certificates" className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-accent"><Award className="h-4 w-4" /> Certificates</button>
          {isStaff && <button onClick={() => navigate("/admin/labs")} data-testid="portal-admin-labs" className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-accent"><ShieldCheck className="h-4 w-4" /> Lab Admin</button>}
          {data?.role === "admin" && <button onClick={() => navigate("/admin")} data-testid="portal-admin" className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-accent"><ShieldCheck className="h-4 w-4" /> Admin Portal</button>}
          {isStaff && <button onClick={() => navigate("/instructor")} data-testid="portal-instructor" className="hidden lg:inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-accent"><Users className="h-4 w-4" /> Instructor</button>}
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="portal-theme-toggle" className="text-muted-foreground">
            {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </Button>
          <div className="h-6 w-px bg-border mx-1" />
          <div className="text-right hidden sm:block"><div className="text-sm font-medium leading-none">{user?.name}</div><div className="text-[11px] text-muted-foreground uppercase tracking-wide mt-0.5">{user?.role}</div></div>
          <Button variant="outline" size="sm" className="gap-2" data-testid="portal-signout" onClick={() => { logout(); navigate("/login"); }}><LogOut className="h-4 w-4" /></Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-10 space-y-8">
        {/* welcome */}
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Welcome, {data?.student_name || user?.name || "Student"}</h1>
          <p className="text-muted-foreground mt-2">Select a lab environment below to begin your hands-on training. Each lab is a fully independent environment.</p>
        </div>

        {/* search + filters */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative sm:max-w-xs w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search Labs" className="pl-9" data-testid="portal-search" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {categories.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`filter-${c}`}
                className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${cat === c ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:bg-accent"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* lab icon tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="lab-grid">
          {filtered.map((lab) => <LabTile key={lab.id} lab={lab} onLaunch={launch} />)}
          {filtered.length === 0 && <div className="col-span-full text-center text-sm text-muted-foreground py-12 border border-dashed border-border rounded-xl">No labs match your search.</div>}
        </div>

        {/* Azure sub-entries (kept accessible, low-key) */}
        {labs.some((l) => l.id === "azure") && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground pt-2 border-t border-border">
            <span className="text-xs uppercase tracking-wider">Azure also includes</span>
            <button onClick={() => navigate("/az900")} data-testid="link-az900" className="inline-flex items-center gap-1.5 hover:text-foreground"><BadgeCheck className="h-4 w-4" /> AZ-900 Exam</button>
            <button onClick={() => navigate("/guided")} data-testid="link-guided" className="inline-flex items-center gap-1.5 hover:text-foreground"><GraduationCap className="h-4 w-4" /> Cloud Engineer Labs</button>
          </div>
        )}
      </div>
    </div>
  );
}

function LabTile({ lab, onLaunch }) {
  const maint = lab.availability === "maintenance";
  const locked = lab.locked && !maint;
  const StatusIcon = lab.lab_status === "completed" ? CheckCircle2 : lab.lab_status === "in_progress" ? PlayCircle : null;
  return (
    <button
      onClick={() => onLaunch(lab)}
      disabled={maint}
      data-testid={`launch-${lab.id}`}
      title={locked ? `${lab.name} — locked (subscribe to unlock)` : lab.name}
      className="group flex flex-col items-center gap-3 focus:outline-none disabled:cursor-not-allowed"
    >
      <div className="relative w-full aspect-square max-w-[160px]">
        <img
          src={ICON_IMG[lab.id]}
          alt={lab.name}
          className={`w-full h-full object-cover rounded-3xl border border-border shadow-sm transition-all duration-200 group-hover:-translate-y-1 group-hover:shadow-xl ${maint ? "opacity-40 grayscale" : ""} ${locked ? "opacity-60" : ""}`}
          style={maint ? {} : { boxShadow: `0 0 0 0 ${lab.accent}` }}
        />
        <span className="pointer-events-none absolute inset-0 rounded-3xl ring-2 ring-transparent group-hover:ring-[color:var(--acc)] transition-all" style={{ ["--acc"]: lab.accent }} />
        {maint && (
          <span className="absolute inset-0 grid place-items-center">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-500/90 text-black px-2.5 py-1 rounded-full"><Wrench className="h-3.5 w-3.5" /> Maintenance</span>
          </span>
        )}
        {locked && (
          <span className="absolute -top-1.5 -right-1.5 h-7 w-7 rounded-full bg-card border border-border grid place-items-center shadow" data-testid={`lock-${lab.id}`} title="Subscribe to unlock">
            <Lock className="h-4 w-4 text-muted-foreground" />
          </span>
        )}
        {!maint && !locked && StatusIcon && (
          <span className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-card border border-border grid place-items-center shadow"
            title={lab.lab_status === "completed" ? "Completed" : "In progress"}>
            <StatusIcon className={`h-4 w-4 ${lab.lab_status === "completed" ? "text-emerald-500" : "text-amber-500"}`} />
          </span>
        )}
      </div>
      <span className="text-sm font-semibold text-center">{lab.short}</span>
    </button>
  );
}
